import React, { useState, useMemo, useEffect } from 'react';
import {
  Accordion, Form, Badge, Button, Card,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import MaterialTable from '@material-table/core';
import { MTableHeader } from 'material-table';

import Path from '../../Path';
import { styledTableHeading, years } from '../../../helpers';
import useAPI from '../../../hooks/useAPI';
import DownloadLink from '../../common/DownloadLink';
import { localization, modifyDefaultOptions, tableIconsModified } from '../../../helpers/table';
import { getColumns } from '../helpers/DescriptionTableHelper';
import { formatTripleSpaced, floatRenderer } from '../../../services/formatSvc';

const upperAccordionContentStrategy = [
  { title: 'Цель стратегического проекта', field: 'goal' },
  { title: 'Задачи стратегического проекта', field: 'tasks' },
  { title: 'Ожидаемые результаты стратегического проекта', field: 'results' },
  { title: 'Описание стратегического проекта', field: 'descr' },
];

const upperAccordionContentPolicy = [
  { title: 'Описание политики', field: 'descr' },
];

const upperAccordionContentGoal = [
  { title: 'Описание содержания стратегической цели развития университета', field: 'descr' },
  { title: 'Целевые качественные и количественные показатели (индикаторы) достижения стратегической цели развития университета', field: 'goalAchievementKpis' },
  { title: 'Описание стратегии достижения стратегической цели развития', field: 'goalAchievementDescr' },
];

const upperAccordionContentTech = [
  { title: 'Цель и задачи реализации стратегического технологического проекта', field: ['goal', 'tasks'] },
  { title: 'Описание стратегического технологического проекта', field: 'descr' },
  { title: 'Ключевые результаты стратегического технологического проекта', field: 'results' },
];

const getUpperAccordionContent = (type) => {
  switch(type) {
    case 'strategy':
      return upperAccordionContentStrategy;
    case 'policy':
      return upperAccordionContentPolicy;
    case 'goal':
      return upperAccordionContentGoal;
    case 'tech':
      return upperAccordionContentTech;
    default:
      return [];
  }
};

const lowerAccordionContent = (type) => {
  const baseContent = [
    {
      title: 'Плановое количество проектов', getQuantity: (statsData) => statsData.countPlan, type: 'шт', collapsible: false, show: type === 'strategy'
    },
    {
      title: 'Проекты, реализуемые в выбранном периоде', getQuantity: (statsData) => statsData.countPeriod, type: 'шт', collapsible: true, tableKey: 'project', show: true
    },
    {
      title: 'Из них, одобренные на ЭГ', getQuantity: (statsData) => statsData.countApproved, type: 'шт', collapsible: false, show: true
    },
    {
      title: 'Уникальные результаты, продукты, услуги', getQuantity: (statsData) => statsData.countResults, type: 'шт', collapsible: true, tableKey: 'result', show: type !== 'policy'
    },
    {
      title: 'План по бюджету из гранта', getQuantity: ({ countFinance }) => (countFinance ? formatTripleSpaced(floatRenderer(countFinance)) : null), type: 'руб', collapsible: false, show: type === 'tech' || type === 'goal'
    },
    {
      title: (type === 'tech' || type === 'goal') ? 'Одобренный бюджет из гранта на ЭГ' : 'Финансирование', getQuantity: ({ projFinance }) => (projFinance ? formatTripleSpaced(floatRenderer(projFinance)) : null), type: 'руб', collapsible: true, tableKey: 'finance', show: type !== 'policy'
    },
    {
      title: 'Одобренный бюджет софинансирования', getQuantity: ({ projFinanceSf }) => (projFinanceSf ? formatTripleSpaced(floatRenderer(projFinanceSf)) : null), type: 'руб', collapsible: false, show: type === 'tech' || type === 'goal'
    },
  ];

  return baseContent.filter(item => item.show);
};

const periods = years.map((y) => ({ value: y, label: y }));

const DEFAULT_YEAR = 2025;

export default function TabDescription({ data }) {
  const { stratId } = useParams();

  const [selectedPeriod, setSelectedPeriod] = useState({ label: DEFAULT_YEAR, value: DEFAULT_YEAR });
  const [statsData, setStatsData] = useState({});

  const columns = useMemo(() => getColumns(selectedPeriod.value, data.type), [selectedPeriod.value, data.type]);

  const api = useAPI();

  useEffect(async () => {
    const resp = await api.get(`/info/strategies/${stratId}/stats?period=${selectedPeriod.value}`);
    if (resp && !resp.error) setStatsData(resp);
  }, [selectedPeriod]);

  const handlePeriodChange = (newSelectedPeriod) => {
    setSelectedPeriod(newSelectedPeriod);
  };

  return (
    <div className="tab-description">
      <section style={styledSection}>
        <Accordion defaultActiveKey="0">
          {getUpperAccordionContent(data.type).map((item, index) => (
            <Accordion.Item eventKey={index} key={index}>
              <Accordion.Header>{item.title}</Accordion.Header>
              <Accordion.Body>
                <p dangerouslySetInnerHTML={{ __html: (Array.isArray(item.field) 
                  ? item.field.map(field => data[field]).filter(Boolean).join('<br><br>')
                  : data[item.field] || '').replace(/\n/gi, '<br>') }} />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </section>
      
      <section style={styledSection}>
        <Path type={data.type}/>
      </section>

      <section style={styledSection}>
        <Form.Group className="mb-3">
          <div style={styledSelectAndButtonContainer}>
            <div>
              <Form.Label>Период</Form.Label>
              <Select
                placeholder="Выберите год"
                name="yearsSingle"
                options={periods}
                onChange={handlePeriodChange}
                value={selectedPeriod}
                isSearchable={false}
                styles={customStyles}
              />
            </div>
            {data.type === 'strategy' && <DownloadLink
              style={{ marginLeft: 16 }}
              variant="outline-primary"
              url={`/info/strategies/${stratId}/export?period=${selectedPeriod.value}`}
              filename={`ЦП_${data.num}.xlsx`}
            >
              Сформировать выгрузку
            </DownloadLink>}
          </div>
        </Form.Group>
      </section>

      <section style={{ ...styledSection, marginBottom: 0 }}>
        <Accordion defaultActiveKey="0">
          {lowerAccordionContent(data.type).map((item, index) =>
            (item.collapsible
              ? (
                <Accordion.Item eventKey={index} key={index}>
                  <Accordion.Header>
                    <AccordionHeaderContent
                      title={item.title}
                      quantity={item.getQuantity(statsData)}
                      type={item.type}
                    />
                  </Accordion.Header>
                  <Accordion.Body>
                    <MaterialTable
                      components={{
                        Header: (props) => (<MTableHeader {...props} style={{ position: 'sticky' }} />),
                      }}
                      options={modifyDefaultOptions({
                        actionsColumnIndex: -1,
                        toolbar: false,
                        filtering: false,
                        maxBodyHeight: 'calc(100vh - 442px)',
                        headerStyle: { height: 50, zIndex: 0 },
                        filterCellStyle: {
                          position: 'sticky',
                          top: 49,
                          background: 'white',
                        },
                      })}
                      icons={tableIconsModified}
                      localization={localization}
                      columns={columns[item.tableKey]}
                      data={statsData.projects}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                <Card>
                  <Card.Header>
                    <AccordionHeaderContent
                      title={item.title}
                      quantity={item.getQuantity(statsData)}
                      type={item.type}
                    />
                  </Card.Header>
                </Card>
              )))}
        </Accordion>
      </section>
    </div>
  );
}

function AccordionHeaderContent(props) {
  return (
    <div style={styledBadgeContainer}>
      <div>{props.title}</div>
      { props.quantity !== null
      && typeof props.quantity !== 'undefined'
      && <Badge style={styledBadge} bg="primary">{`${props.quantity} ${props.type}.`}</Badge>}
    </div>
  );
}

const styledSection = { marginBottom: 32, color: 'initial' };
const styledSelectAndButtonContainer = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 3,
};
const styledBadgeContainer = {
  display: 'flex', justifyContent: 'space-between', width: '100%', marginRight: 21, color: '#212529',
};
const styledBadge = {
  minWidth: 160, padding: 7, margin: -5, fontSize: 18,
};
const customStyles = {
  menu: (provided) => ({ ...provided, width: 200 }),
  control: (provided) => ({ ...provided, width: 200 }),
};
